// Field names follow STE-536 Part 4 exactly, including `quals` rather than
// `qualifications`, so nothing has to be renamed when the API lands.

export const LEVELS = ['Junior', 'Mid', 'Senior', 'Staff', 'Principal'] as const
export type Level = (typeof LEVELS)[number]

export const STATUSES = ['Draft', 'In review', 'Published'] as const
export type JdStatus = (typeof STATUSES)[number]

/**
 * Super admin is not a bigger admin. It sets up companies and the people in
 * them, and it cannot open a job description or a single salary figure.
 */
export type Role = 'superadmin' | 'admin' | 'manager'

/**
 * The two states a person can choose when saving. "In review" is set by the
 * approval flow, never by someone pressing save.
 */
export type SaveStatus = 'Draft' | 'Published'

export interface Me {
  userId: string
  name: string
  email: string
  role: Role
  tenant: { slug: string; name: string }
  settings: { jobFamilies: string[]; levels: Level[]; currency: string }
  pendingApprovals: number
  jdCount: number
}

export interface VersionEntry {
  version: number
  date: string
  note: string
  by: string
}

/**
 * Whether a role's pay figures are a yearly salary or an hourly rate.
 *
 * **The two scales are different and nothing converts between them.** A yearly
 * figure is in thousands, so `170` means $170,000. An hourly figure is whole
 * currency, so `45` means $45 an hour.
 *
 * That is two meanings for one field, which is the shape of mistake this
 * codebase has been bitten by before, so it is declared here and the
 * formatters are the only things allowed to know it. Nothing multiplies an
 * hourly rate out to a year: **every assumed working year is somebody's
 * assumption**, and this product does not make those on a customer's behalf.
 *
 * Absent on every role saved before this existed, and absent reads as `year`,
 * which is what they have always meant.
 */
export type PayUnit = 'year' | 'hour'

export interface JobDescription {
  id: string
  title: string
  family: string
  level: Level
  location: string
  payMin: number
  payMax: number
  /** What the two figures above mean. Absent reads as `year`. See `PayUnit`. */
  payUnit?: PayUnit
  summary: string
  responsibilities: string[]
  skills: string[]
  quals: string[]
  status: JdStatus
  version: number
  updated: string
  industry: string
  /*
   * The sections a job description ends with, read out of the document and
   * kept. A company publishing a role has to publish its physical demands
   * and its equal opportunity declaration with it.
   *
   * Nothing that prices a role reads any of these, so none of them appears
   * in a change request or in anything the matcher is shown. Optional,
   * because most documents carry none and every role saved before this has
   * none.
   */
  physicalDemands?: string
  workEnvironment?: string
  totalRewards?: string
  /** What the document says about how pay is decided. The figures are payMin and payMax. */
  compensationNote?: string
  /** Equal opportunity, sponsorship and the rest, word for word as published. */
  legal?: string
  /**
   * The document as it was written: its own headings and every line under
   * each, in order. Shown to people; nothing that matches or prices a role
   * reads it. Absent on a role that did not come from a document.
   */
  sections?: DocSection[]
  sourceFile?: { fileName: string; extractedBy?: string } | null
  createdBy?: string
  updatedBy?: string
}

/** One section of a document: its heading as written, and its lines word for word. */
export interface DocSection {
  heading: string
  lines: string[]
}

/**
 * What a manager may propose a change to (FR-17a). Family is not one. The
 * form proposes the key details and the sections; the four text fields stay
 * so a request raised before the form showed sections can still be shown.
 */
export const PROPOSABLE_FIELDS = [
  'title',
  'level',
  'location',
  'payMin',
  'payMax',
  'summary',
  'responsibilities',
  'skills',
  'quals',
  'sections',
] as const
export type ProposableField = (typeof PROPOSABLE_FIELDS)[number]

export type ChangeValue = string | number | string[] | DocSection[]

export interface ChangeRequest {
  id: string
  jdId: string
  jdTitle: string
  requestedBy: string
  requestedByRole: string
  requestedAt: string
  reason: string
  changes: Partial<Record<ProposableField, ChangeValue>>
  baseVersion: number
  status: 'pending' | 'approved' | 'rejected'
  decidedBy?: string
  decidedAt?: string
  decisionNote?: string
}

/** A cursor-paged list. There are no page numbers by design (STE-536 Part 3). */
export interface Page<T> {
  items: T[]
  cursor: string | null
  total: number
  matched: number
  /** Present when a page was asked for by number: the page served, from 0. */
  page?: number
  /** Present with `page`: how many pages the result makes. */
  pages?: number
}

export type UploadStatus =
  | 'uploaded'
  | 'extracting'
  | 'validated'
  | 'ready'
  | 'failed'
  | 'imported'
  /** A survey commit was accepted and a worker is writing the rows. */
  | 'storing'

export interface BulkRow {
  line: number
  title: string
  family: string
  level: string
  location: string
  payMin: number
  payMax: number
  summary: string
  responsibilities: string
  skills: string
  quals: string
  status: string
  errors: string[]
  /** The sector the employer operates in, worked out from the document. */
  industry?: string
  /** Where the row came from. A spreadsheet is read, a document is interpreted. */
  from?: 'csv' | 'model'
  /** Page of the source document this role started on. Documents only. */
  page?: number
  /**
   * Fields that were not copied out of the document but arrived some other
   * way. For a document that never states a job family or a level, and most do
   * not, these are the fields a person actually has to look at.
   */
  guessed?: string[]
  /** Anything about this role a person should know before importing it. */
  notes?: string
  /**
   * How many other job descriptions already carry this title.
   *
   * Not an error. A company really does have two roles called the same thing.
   * It is said on the row because the title is no longer what stops a repeated
   * import, so seeing it is the only signal left.
   */
  sameTitle?: number
  /**
   * The role's sections: as the document wrote them, or as a person changed
   * them in the Edit panel (FR-57, FR-59). A spreadsheet row has none until
   * its text is changed, and shows sections built from its columns (FR-61).
   */
  sections?: DocSection[]
}

/** What a document turned out to be when it was read. */
export type DocsetKind = 'job_descriptions' | 'level_matrix' | 'neither'

export interface DocsetOutcome {
  kind: DocsetKind
  note: string
  pagesTotal?: number
  pagesDone?: number
  /** Entries offered that were not roles, named rather than binned. */
  dropped?: string[]
  /** How many roles the reader said the document holds. */
  claimed?: number
  /** Roles the document lists that were still not read when asked for by name (FR-63). */
  missing?: number
  /** Their titles. */
  unread?: string[]
}

export interface UploadJob {
  id: string
  kind: 'single' | 'bulk' | 'docset' | 'market'
  fileName: string
  status: UploadStatus
  /** Present on a survey upload. */
  market?: MarketJobDetail | null
  possibleDuplicates?: PossibleDuplicate[]
  /** single-document extraction result, for a person to check before saving (FR-8) */
  draft?: ReviewDraft | null
  /** bulk validation result (FR-11, FR-13) */
  rows?: BulkRow[]
  summary?: { ready: number; errors: number }
  /** How many rows the file held, once it has been split. */
  rowCount?: number
  /**
   * How far an import has got.
   *
   * A big file needs several commits, because one request cannot outlast the
   * gateway, and this is where the next one picks up.
   */
  importCursor?: number
  /** Present on a document upload. What we made of the file. */
  docset?: DocsetOutcome | null
  error?: string | null
  createdAt: string
}

/** The shape the extraction form is edited in: list fields are plain text. */
export interface JdDraft {
  title: string
  family: string
  level: string
  /**
   * The sector the employer operates in.
   *
   * It used to be derived from the job family through a fixed map. Families are
   * no longer drawn from a fixed list, so the map cannot be right and would
   * label every pharmaceutical role Technology. The reader works it out from
   * the document instead, and a person can change it.
   */
  industry: string
  location: string
  payMin: string
  payMax: string
  /** `year` or `hour`. Empty reads as a year, like a role saved before this existed. */
  payUnit: string
  summary: string
  responsibilities: string
  skills: string
  quals: string
}

/**
 * A draft read from a document: the fields the reader filled, the parts the
 * document ended with, and the document's own sections where they were read.
 */
export type ReviewDraft = JdDraft & {
  physicalDemands?: string
  workEnvironment?: string
  totalRewards?: string
  compensationNote?: string
  legal?: string
  sections?: DocSection[]
}

/**
 * What a save of one role sends (FR-47, FR-50).
 *
 * The key details, and the sections as the form holds them. The hidden
 * fields ride along only when an upload read them, together with the
 * sections they were read from, so the server can tell whether the person
 * changed the text and the fields need reading again. The form never sends
 * them on an edit: the server keeps what the role has and reads them again
 * when the sections change.
 */
export interface RoleSave {
  title: string
  family: string
  level: string
  industry: string
  location: string
  payMin: string
  payMax: string
  payUnit: string
  sections?: DocSection[]
  fieldsReadFrom?: DocSection[]
  summary?: string
  responsibilities?: string
  skills?: string
  quals?: string
  physicalDemands?: string
  workEnvironment?: string
  totalRewards?: string
  compensationNote?: string
  legal?: string
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ---------------------------------------------------------------------------
// Market data.
//
// There is no schema here and that is the point. Mercer, Radford, WTW and Korn
// Ferry each publish their own layout, and a client licenses a slice of one, so
// a file arrives with eleven columns or with twenty two and they are not the
// same eleven. Nothing below names a pay field, a percentile or a benchmark
// title, and no screen may assume a particular column exists.
// ---------------------------------------------------------------------------

export const COLUMN_KINDS = ['text', 'number', 'money'] as const
export type ColumnKind = (typeof COLUMN_KINDS)[number]

export interface SurveyColumn {
  /** What values are stored under. Never shown to anybody. */
  key: string
  /** The provider's own heading. This is what appears as a column title. */
  heading: string
  /**
   * What we read that heading as, in plain words.
   *
   * `Base Sal Ann 50th Pctl USD` becomes "Base salary, annual, 50th
   * percentile". This is what she checks and corrects on the confirm step.
   */
  name: string
  kind: ColumnKind
  /** Only where the heading itself named one. Never invented. */
  currency?: string
}

export interface SurveyFile {
  fileName: string
  addedBy: string
  addedAt: string
  rowCount: number
}

export interface SurveyFilter {
  key: string
  heading: string
  values: string[]
  /** Too many distinct values to list, so this column is narrowed by typing. */
  tooMany?: boolean
}

/**
 * What one narrowed column requires.
 *
 * It names the keys it covers rather than one key, because a column on the
 * every-survey table is one heading drawn from several surveys, and the same
 * heading sits at a different position in each file so it has a different key
 * in each. A row passes when any one of those keys matches.
 */
export interface FilterClause {
  keys: string[]
  /** Values the column may hold, from its own list. */
  any?: string[]
  /** Values the column may not hold. The same filter said the other way round. */
  none?: string[]
  /** Text the column has to contain, for a column with no list. */
  contains?: string
}

/** One column's values, fetched when its filter is opened. */
export interface ColumnValues {
  key: string
  heading: string
  values: string[]
  tooMany: boolean
  /** The survey was too large to read inside one request, so this is a start. */
  partial?: boolean
}

/**
 * Which column holds each field the table shows.
 *
 * **Resolved on the server**, by the same functions the benchmarking index
 * and the matcher use, and sent with the survey. There is no second copy of
 * those rules here: the patterns in them are subtle enough to have been wrong
 * twice, and a screen reading a different column from the one the agent
 * reasons over lets a figure look right and be matched on something else.
 *
 * A field the provider's file does not carry is absent, and its column is
 * blank for that survey. The set of columns is the same either way.
 */
export interface ShownColumns {
  code?: string
  title?: string
  family?: string
  level?: string
  country?: string
  incumbents?: string
  orgs?: string
  p25?: string
  p50?: string
  p75?: string
  p90?: string
  /** The bonus-inclusive total and the equity, each its own measure. */
  cash25?: string
  cash50?: string
  cash75?: string
  cashMeasure?: string
  equity25?: string
  equity50?: string
  equity75?: string
  equityMeasure?: string
  currency?: string
  unit: 'annual' | 'hourly'
  /** What the chosen measure is called, so the pay columns say which one they are. */
  measure?: string
}

/** The fields the table shows, in the order it shows them. */
export const SHOWN_FIELDS = [
  'code', 'title', 'family', 'level', 'country',
  'incumbents', 'orgs',
  'p25', 'p50', 'p75', 'p90',
  // Base pay first, then what a bonus adds, then equity. A reader goes left
  // to right from the narrowest figure to the fullest.
  'cash25', 'cash50', 'cash75',
  'equity25', 'equity50', 'equity75',
] as const
export type ShownField = (typeof SHOWN_FIELDS)[number]

export interface MarketSurvey {
  id: string
  provider: string
  year: string
  industry: string
  uploadedBy: string
  uploadedAt: string
  rowCount: number
  files: SurveyFile[]
  columns: SurveyColumn[]
  filters: SurveyFilter[]
  /** Which column holds each shown field. Absent on a survey served before this existed. */
  shown?: ShownColumns
  /** Being emptied by a background job. It stays listed while that runs. */
  removing?: boolean
  /** A file's rows are being written into it. */
  storing?: boolean
  /** While storing: how many rows are in, out of how many will be (FR-M58). */
  progress?: { stored: number; of: number }
  /** Once stored: how far preparing it for matching has got (FR-M59). */
  preparation?: {
    state: 'preparing' | 'ready' | 'empty' | 'stopped'
    /** Rows read across both readings, out of both readings together. */
    rows: number
    of: number
    at: string
  }
}

export interface MarketRecord {
  id: string
  survey: string
  line: number
  sourceFile: string
  sourceLine: number
  /** Keyed by column key. Every value exactly as the provider's file wrote it. */
  values: Record<string, string>
  updatedBy?: string
  updatedAt?: string
}

/**
 * A page of market records.
 *
 * **There is deliberately no total match count.** Search reads the table a page
 * at a time, so how many rows match is not known until the last page has been
 * read. `shown` is what came back, `held` is what the company has, and `more`
 * says whether to offer another page. No screen may invent a total.
 */
export interface MarketPage {
  items: MarketRecord[]
  cursor: string | null
  shown: number
  held: number
  more: boolean
}

/** A survey already held that an upload might be repeating. A warning only. */
export interface PossibleDuplicate {
  id: string
  provider: string
  year: string
  fileName: string
  rowCount: number
  uploadedAt: string
  matchedOn: 'provider and year' | 'file name'
}

/** What a survey upload knows about itself before any row is stored. */
export interface MarketJobDetail {
  provider: string
  year: string
  industry: string
  surveyId?: string
  columns?: SurveyColumn[]
  /** One real value per column, from her own file, so names are checkable. */
  samples?: Record<string, string>
  filters?: SurveyFilter[]
  /** Both capped, with the full count kept. Never render more than is sent. */
  unreadable?: { count: number; shown: { line: number; reason: string }[] }
  thin?: { count: number; shown: number[] }
  rowsRead?: number
  rowsWritten?: number
  newColumns?: string[]
}


/* ---------- setting up companies ---------- */

/** The two roles a super admin can give somebody. Never super admin. */
export const GIVEABLE_ROLES = ['admin', 'manager'] as const
export type GiveableRole = (typeof GIVEABLE_ROLES)[number]

export interface AdminCompanySummary {
  slug: string
  name: string
  createdAt: string | null
  people: number
  domains: string[]
}

export interface AdminPerson {
  userId: string
  tenant: string
  email: string
  name: string
  role: Role
  addedBy?: string
  addedAt?: string
  disabled?: boolean
  /** False while they are still on the password they were given. */
  signedIn?: boolean
  /** In the table but with no login behind it. Shown, never hidden. */
  noLogin?: boolean
  /** Logs in with their company single sign-on, so has no password (STE-1022). */
  sso?: boolean
}

export interface AdminHistoryEntry {
  id: string
  at: string
  byName: string
  byEmail: string
  action:
    | 'company.created'
    | 'domain.added'
    | 'domain.removed'
    | 'person.added'
    | 'person.role'
    | 'person.access'
    | 'person.password'
    | 'person.password.set'
    | 'company.sso'
  tenant: string
  personEmail?: string
  detail?: Record<string, string>
}

export interface AdminCompanyDetail {
  company: {
    slug: string
    name: string
    createdAt: string | null
    /** The single sign-on connection its people log in through, or null for passwords (STE-1022). */
    ssoConnection?: string | null
  }
  domains: string[]
  people: AdminPerson[]
  history: AdminHistoryEntry[]
  /** Whether this environment can use company single sign-on at all. Only through Auth0. */
  ssoAvailable?: boolean
  /** Whether super admin can set a person's password directly. Only through Auth0 (STE-1044). */
  setPasswordAvailable?: boolean
}

/** What comes back once, and is never retrievable again. */
export interface NewLogin {
  person: AdminPerson
  /** A temporary password to pass on, through Cognito. */
  password?: string
  /** Through Auth0 there is no password: the person was emailed a link (STE-1013). */
  emailed?: boolean
  /** At a single sign-on company nothing is sent: they log in with their company login (STE-1022). */
  sso?: boolean
}

/* ---------- benchmarking ---------- */

/** One survey cut that went into a band, with its own figures kept. */
/** The seven named reasons a match carries, in the order they are shown. */
export interface WhyPicked {
  content: string
  levelling: string
  skills: string
  industry: string
  sample: string
  currency: string
  practice: string
}

/** The headings, in order. Kept beside the type so the two cannot drift. */
export const WHY_ORDER: { key: keyof WhyPicked; label: string }[] = [
  { key: 'content', label: 'Content match' },
  { key: 'levelling', label: 'Levelling' },
  { key: 'skills', label: 'Skill and technology signal' },
  { key: 'industry', label: 'Industry and competitive set' },
  { key: 'sample', label: 'Sample robustness' },
  { key: 'currency', label: 'Data currency and aging' },
  { key: 'practice', label: 'How the market is matching right now' },
]

/** The steps the matcher works through, in the order it works in them. */
export interface HowMatched {
  ladder: string
  role: string
  searched: string
  covers: string
  level: string
  gaps: string
}

export const HOW_ORDER: { key: keyof HowMatched; label: string }[] = [
  { key: 'ladder', label: 'Manager or individual contributor' },
  { key: 'role', label: 'What this role is' },
  { key: 'searched', label: 'What was searched for' },
  { key: 'covers', label: 'Whether this survey covers the work' },
  { key: 'level', label: 'Which level, and why' },
  { key: 'gaps', label: 'What did not line up' },
]

export interface BenchmarkCut {
  code: string
  title: string
  incumbents: number
  orgs: number
  /** Its share of the blend, weighted by the people behind it. 0 when not in the band. */
  weight: number
  /**
   * How much a person wants this cut to count, as a multiplier on that share.
   * 1, or absent, is the default.
   */
  emphasis?: number
  /**
   * Whether this cut is in the band. Absent on a benchmark saved before the
   * whole shortlist was kept, where every stored cut was a chosen one.
   */
  chosen?: boolean
  /** How confident the match was, 0 to 1. */
  score: number
  reason: string
  /**
   * The reasons under the headings a person asks them in.
   *
   * Absent on a benchmark saved before this existed, where `reason` is one
   * paragraph answering all of them at once. The screen shows that paragraph
   * instead rather than leaving the card blank.
   */
  why?: WhyPicked
  /** The relationship in one line, for somebody who reads nothing else. */
  relation?: string
  /** The provider's own definition, exactly as published. Absent where none. */
  surveyDescription?: string
  /** Who published it and for which year, so a person can weigh the source. */
  provider?: string
  year?: string
  location?: string
  p25: number | null
  p50: number | null
  p75: number | null
  p90: number | null
}

/**
 * A band, holding only the percentiles the survey reported.
 *
 * A missing one is null and is not shown. It used to be filled in from its
 * neighbour, so a survey with no 90th percentile column printed its 75th under
 * a heading of 90TH, and a cut reporting only a median drew the same figure
 * four times. Nothing about that looked wrong, which is why it lasted.
 */
export interface MarketBand {
  p25: number | null
  p50: number
  p75: number | null
  p90: number | null
}

/** One job description measured against this company's own survey. */
export interface Benchmark {
  /**
   * Everything this answer was built from, as one short string: the role's
   * version and the state of the company's market data. While it matches, the
   * answer is reused and nothing is asked again.
   */
  fingerprint?: string
  jdId: string
  jdTitle?: string
  jdVersion: number
  surveyId: string
  cuts: BenchmarkCut[]
  band: MarketBand | null
  unit: string | null
  currency: string | null
  compaRatio: number | null
  position: string
  confidence: string
  confidenceReason: string
  /** What was left out and why. The panel that makes a band defensible. */
  /**
   * What was set aside, counted by reason.
   *
   * An answer saved before this holds one entry per job; a newer one holds
   * one per reason with a count. Both draw, because a listed job counts as
   * one. A large survey listed one job at a time weighs more than a record
   * may hold, which is why the shape changed.
   */
  excluded: ({ code: string; title: string; reason: string } | { reason: string; count: number; examples: string[] })[]
  /** Candidates the match considered and turned down. */
  rejected: { code: string; reason: string }[]
  /**
   * What the matcher was shown, where it was not shown everything eligible.
   *
   * Mirrors `offered` on the backend's `Benchmark`. `of` is how many jobs the
   * survey holds, which is what lets the set-aside panel tell "some were set
   * aside" from "all of them were".
   */
  offered?: { shown: number; of: number; ranked: boolean; whole?: boolean }
  managesPeople: boolean | null
  managesPeopleReason: string
  reasoning: string
  /**
   * The reasoning under the steps the matcher works in.
   *
   * Absent on a benchmark saved before this, and where fewer than two steps
   * came back. The screen shows the paragraph instead.
   */
  how?: HowMatched
  /** Set when nothing was close enough. An answer, not a failure. */
  noMatchReason: string | null
  country: string
  runBy: string
  runAt: string
  model: string
  outOfDate?: boolean
  /** The cuts a person chose, and who chose them. A re-run does not undo this. */
  confirmed?: { codes: string[]; by: string; at: string }
  /**
   * Codes a person had confirmed that the current market data no longer
   * offers, usually because that survey was removed.
   *
   * The choice cannot be honoured against data that is not there, so the
   * model's pick stands and this says which decision was lost. Silently
   * dropping somebody's choice is the thing this whole field exists to stop.
   */
  confirmedLost?: string[]
  /** What a person called this band when they saved it. One name, not a history. */
  name?: string
  /** The AI's answer to HR's reason, when this benchmark came from HR feedback. */
  feedbackAnswer?: string
  /** Set when the attempt broke rather than finding nothing. */
  failedReason?: string
  /** What this role answered last time, when this run answered differently. */
  /** The provider and year, for the screen. Never the internal id. */
  surveyName?: string
  /** How many uploads of that one survey the band could draw on. */
  surveyFiles?: number
  /**
   * The role being matched, so it can be read beside the shortlist.
   *
   * Sending a person to another tab to remember what the role said is how a
   * match gets confirmed without being read.
   */
  profile?: {
    title: string; family: string; level: string; location: string
    payMin: number; payMax: number
    summary: string; responsibilities: string[]; skills: string[]; quals: string[]
    status: string; version: number; updated: string
    /** What that one-word level stands for, in the words the match was made against. */
    levelScope: string
  } | null
}

export type BenchmarkStatus =
  /** Matched, with a band behind it. */
  | 'benchmarked'
  | 'out of date' | 'never run' | 'no match found'
  /** The attempt broke. A different answer from having found nothing. */
  | 'could not be matched'
  /*
   * Kept so a benchmark saved before market position was dropped still
   * renders rather than falling through to nothing.
   */
  | 'at market' | 'above market' | 'below market' | 'no pay range set' | 'not benchmarked'

/** A row on the benchmarking list. */
export interface BenchmarkRow {
  jdId: string
  title: string
  family: string
  level: string
  location?: string
  /** What the market says, not what the role pays. Null until it is benchmarked. */
  median?: number | null
  currency?: string | null
  /** Whether a person has confirmed the match, rather than only a run producing it. */
  confirmed?: boolean
  status: BenchmarkStatus
  compaRatio: number | null
  confidence: string | null
  runAt: string | null
}

export interface BenchmarkOverview {
  /** False for a company that has bought no market data yet. Not an error. */
  hasSurvey: boolean
  /**
   * The run going right now, or null.
   *
   * Null is the normal answer and means no run is going, not that the server
   * did not say. Absent on a response from a deployment before this existed,
   * which reads the same way.
   *
   * Carries no cost, no token count and no price, like everything else about
   * a run that reaches a browser.
   */
  running?: RunningNow | null
  /**
   * The counts describe the whole library, never the page.
   *
   * Two different questions, asked at once. A count that quietly started
   * describing the visible hundred is a number somebody reads out in a
   * meeting.
   */
  /**
   * The five states, and the two the screen shows.
   *
   * `done` and `toDo` add up to the library on purpose, so somebody can check
   * them against the count in the corner without being told how. The five are
   * still here because each row reads its own state from them.
   *
   * Absent on a response from a deployment before the two existed.
   */
  counts: {
    benchmarked: number
    outOfDate: number
    neverRun: number
    noMatch: number
    failed?: number
    done?: number
    toDo?: number
  }
  /** One page of roles, not the library. */
  items: BenchmarkRow[]
  /** Which page this is, counting from zero. */
  page?: number
  /**
   * How many pages there are, exactly.
   *
   * Exact rather than a guess, because the whole library is read to answer
   * the counts anyway. Market Data cannot always say this and offers only the
   * pages it has reached; here every number offered is a page that exists.
   */
  pages?: number
  /** How many roles the company holds. */
  total?: number
  /** How many the search left, which is `total` when nothing is typed. */
  matched?: number
}

/** How far the current run has got, as the overview reports it. */
export interface RunningNow {
  runId: string
  state: RunState
  done: number
  total: number
  /** Time left, never money spent. */
  secondsLeft: number
  startedBy: string
  startedAt: string
}

export type RunState = 'running' | 'stopping' | 'stopped' | 'done'

export interface RunProgress {
  runId: string
  state: RunState
  total: number
  done: number
  failed: number
  /**
   * How many of the failures were our Bedrock allowance rather than the role.
   * Part of `failed`, not instead of it, so the totals still add up.
   */
  throttled: number
  /**
   * How many times a role has stopped to wait for our Bedrock allowance.
   *
   * Nearly always larger than `throttled`, because most waits end in the role
   * succeeding. A count of waits, never a cost.
   */
  waits: number
  noMatch: number
  /** Time left, never money spent. */
  secondsLeft: number
  recent: { jdId: string; title: string; outcome: string; compaRatio: number | null }[]
  startedBy: string
  startedAt: string
  finishedAt: string | null
}



/* ---------- Match a job ---------- */

/** Where one match has got to. */
export type MatchState = 'queued' | 'reading' | 'matching' | 'done' | 'failed' | 'stopped'

/** How the text was read. Null on a follow-up, which is a question not a role. */
export type ReadAs = 'description' | 'title' | 'not-a-job' | null

/** One thing sent and the answer it got back. */
export interface MatchTurn {
  turnId: string
  asked: string
  askedAt: string
  answer: string
  answeredAt: string | null
  readAs: ReadAs
  /**
   * What the answer says about its own certainty.
   *
   * It comes from the answer rather than being worked out here, so that it
   * travels. A copied answer and a downloaded conversation both carry it, and
   * a warning that only lives on the screen stops being true the moment the
   * answer leaves the screen.
   */
  note: string
  /** How many jobs this answer holds, present only when one paste held several. */
  jobs?: number
}

export interface MatchChat {
  chatId: string
  startedAt: string
  turns: MatchTurn[]
  /**
   * The match still running in this conversation, if there is one.
   *
   * It is in no turn yet, because a turn is only written once there is an
   * answer. Without this, coming back to a conversation with a match in flight
   * would show no sign of it.
   */
  running: string | null
}

/** What the progress route says, which is never what a match costs us. */
export interface MatchProgress {
  matchId: string
  chatId: string
  state: MatchState
  /** What it is doing now, in words. */
  stage: string
  startedAt: string
  finishedAt: string | null
  /** The answer as it is written, so the screen is not blank for two minutes. */
  answerSoFar: string
  /** Plain words for the screen. */
  failedReason: string | null
  /** The technical detail, kept behind a closed section. */
  failedDetail: string | null
  readAs: ReadAs
  note: string
  /** How far through a paste of several jobs. Null for a paste of one. */
  jobs?: { done: number; total: number } | null
  /** Time, never money. */
  secondsSoFar: number
}

/* ---------- salary ranges ---------- */

/** Where a role stands, which decides what its row can show. */
export type RangeState = 'banded' | 'no-match' | 'never-run' | 'out-of-date'

/** A median the market is proposing, which nobody has accepted yet. */
export interface RangeWaiting {
  from: number
  to: number
}

/**
 * One role on the salary ranges screen.
 *
 * The minimum and maximum are null unless there is an accepted median to build
 * them from, which is what the row reads to decide whether to show figures at
 * all. `waiting` is null on every role the market agrees with, which is most
 * of them, and is the only thing the Suggested changes tab is built from.
 */
/**
 * The percentiles a benchmark can hold.
 *
 * Four, and no others. The 65th a prototype once offered came from
 * interpolating between two of these, which puts a figure in a pay range that
 * no provider ever stated.
 *
 * **Change this and `backend/src/lib/bands.ts` together.** The frontend does
 * not import backend code, so the two lists are kept in step by hand and by
 * the check that reads both.
 */
export type Pct = 'p25' | 'p50' | 'p75' | 'p90'

export interface RangeRow {
  jdId: string
  title: string
  family: string
  level: Level
  location: string
  state: RangeState
  minimum: number | null
  midpoint: number | null
  maximum: number | null
  /** The provider's own words, per row, because they differ between rows. */
  currency: string | null
  unit: string | null
  /** The width in force for this role, and whether it is this role's own. */
  spread: number
  overridden: boolean
  /** Where this range sits in the market, and what this role can be moved to. */
  percentile: Pct
  /** Built from this role's own benchmark, so it differs from row to row. */
  percentiles: Pct[]
  confidence: string | null
  waiting: RangeWaiting | null
  acceptedBy: string | null
  acceptedAt: string | null
  /** The role was edited since it was benchmarked. Not a suggested change. */
  outOfDate: boolean
}

export interface SalaryRanges {
  hasSurvey: boolean
  /** The company's spread, used by every role that has not been given its own. */
  spread: number
  /**
   * How the whole library stands, not this page.
   *
   * The counts and the rows answer two different questions and this screen
   * asks both at once. A count that quietly started describing the visible
   * twenty would be a number somebody reads out in a meeting.
   */
  counts: { banded: number; unbanded: number }
  /** How many roles have a median waiting, for the count on the tab. */
  waiting: number
  /** Every role in the library. */
  total: number
  /** How many the search left, for "showing 21 to 40 of 412". */
  matched: number
  /** Every family in the library, not only the ones on this page. */
  families: string[]
  /** Which page this is, counted from zero. */
  page: number
  /** How many there are in total, which is known exactly. */
  pages: number
  items: RangeRow[]
}

/**
 * One role's range as it was published.
 *
 * Every figure is carried rather than pointed at, so it still reads the same
 * after the benchmark behind it has been re-run or the spread widened. That is
 * what makes it a record of what was agreed rather than a view of what is true
 * now.
 */
export interface PublishedRange {
  jdId: string
  title: string
  family: string
  level: string
  location: string
  minimum: number
  midpoint: number
  maximum: number
  spread: number
  /** Absent on a set published before a range could sit anywhere but the median. */
  percentile?: Pct
  currency: string | null
  unit: string | null
  survey: string | null
}

/** The published set, or nothing if this company has never published one. */
export interface PublishedSet {
  publishedAt: string | null
  publishedBy: string | null
  items: PublishedRange[]
}

/** One survey job the midpoint was built from. No weight: with one survey there is nothing to weigh. */
export interface RangeCut {
  code: string
  title: string
  provider: string | null
  year: string | null
  location: string | null
  median: number | null
  orgs: number
  incumbents: number
}

/** One role's range, and where its midpoint came from. */
export interface RangeDetail {
  jdId: string
  title: string
  family: string
  level: Level
  location: string
  state: RangeState
  minimum: number | null
  midpoint: number | null
  maximum: number | null
  spread: number
  overridden: boolean
  /** The company's, so the control can offer a way back to it. */
  companySpread: number
  percentile: Pct
  percentiles: Pct[]
  currency: string | null
  unit: string | null
  confidence: string | null
  survey: string | null
  cuts: RangeCut[]
  /** Said here, decided on the Suggested changes tab. */
  waiting: RangeWaiting | null
  acceptedBy: string | null
  acceptedAt: string | null
  outOfDate: boolean
  noMatchReason: string | null
}

/* ---------- HR feedback on a benchmark (STE-991, STE-992) ---------- */

export type FeedbackState = 'queued' | 'matching' | 'ready' | 'failed' | 'stale'

/** A re-match with HR's reason, waiting beside the benchmark until HR chooses. */
export interface PendingFeedback {
  feedbackId: string
  jdId: string
  reason: string
  surveyJob?: string
  by: { sub: string; name: string }
  at: string
  state: FeedbackState
  base: { runAt: string; fingerprint: string; chosenCodes: string[] }
  /** The AI's own answer to the reason, worded as it wrote it. */
  feedbackAnswer?: string
  failedReason?: string
  /** The new answer, once there is one. */
  answer?: Benchmark
}

/** One request in the record kept with the role. */
export interface FeedbackRecord {
  feedbackId: string
  jdId: string
  reason: string
  surveyJob?: string
  by: { sub: string; name: string }
  at: string
  before: string[]
  after?: string[]
  decision: 'waiting' | 'kept new' | 'kept current' | 'failed'
  decidedAt?: string
}

export interface FeedbackView {
  pending: PendingFeedback | null
  history: FeedbackRecord[]
}

/* ---------- Iconic, the AI agent (STE-989) ---------- */

export type IconicTurnState = 'queued' | 'thinking' | 'done' | 'failed' | 'stopped'
export type IconicActionState = 'waiting' | 'running' | 'done' | 'failed' | 'cancelled'

/** One of a person's own chats, as the list shows it. */
export interface IconicChat {
  chatId: string
  title: string
  titleSetByPerson: boolean
  startedAt: string
  updatedAt: string
  turnCount: number
  lastScreen?: string
  /** The answer being worked on, if there is one. */
  running?: string
}

/** Something Iconic proposed, waiting for the person's OK. Never the call behind it. */
export interface IconicAction {
  actionId: string
  kind: string
  /** The card: its first line is the title, the rest are what will happen. */
  summary: string[]
  warning?: string
  state: IconicActionState
  proposedAt: string
  link?: string
  result?: string
  doneAt?: string
}

export interface IconicTurn {
  turnId: string
  seq: number
  kind: 'question' | 'answer'
  text: string
  at: string
  screen?: string
  record?: string
  state?: IconicTurnState
  /** What it is doing now, in plain words: "Reading Benchmarking". */
  doing?: string
  failedReason?: string
  answeredAt?: string
  /** Which tools ran, by name. Never what they returned. */
  tools?: string[]
  action?: IconicAction
}

export interface IconicChatView {
  chat: IconicChat
  turns: IconicTurn[]
  /** Older turns exist. */
  more: boolean
}
